import { History } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  productWarnings,
  recencySentence,
  stockSentence,
} from "@/lib/on-behalf-warnings"
import { cn } from "@/lib/utils"
import type {
  EnrolOffer,
  EnrolOfferProduct,
  PreviewCreator,
} from "@/types/mission-assign"

/**
 * OKS-1042 — « Inscrire pour le créateur » (UX Spec §2.2).
 *
 * Four `fieldset`s in order — creator, product, variant, address — each
 * revealed only once the previous is answered.
 *
 * ⚠️ REVEALED, NOT DISABLED. A control that is present but dead is the OKS-988
 * F1 pattern: the brand clicks it, nothing happens, and nothing says why. The
 * variant step is absent rather than empty when the product has no real
 * choice, and the product step is replaced by a sentence when the drop offers
 * one product (PRD OQ8).
 *
 * ⚠️ WARNINGS NEVER DISABLE AN OPTION. The whole point of R5 is that the brand
 * may proceed: exhausted stock and « a déjà reçu » are text under the card,
 * `aria-describedby`-linked to their radio, and the radio stays selectable.
 * A disabled option would silently turn R5's warn-never-refuse into a refusal
 * at the surface while the server still accepted it.
 *
 * ⚠️ This is the OKS-30 hand-pick roster in SINGLE-select. Same search, same
 * rows, `RadioGroup` in place of `Checkbox` (UX UQ1). Creators already on the
 * mission render disabled with a reason — and the frame that proves it needs a
 * SELECTABLE sibling beside them, which is why they are disabled rather than
 * hidden (PRD OQ7).
 *
 * State lives here and is lifted to the page only as the staged answers the
 * confirm dialog posts as hidden fields. Nothing here writes; the dialog owns
 * the single Inertia `<Form>` (UX UQ3).
 */
export interface OnBehalfAnswers {
  crewMembershipId: number | null
  missionProductId: number | null
  productVariantId: number | null
  address: Record<string, string>
}

export const ON_BEHALF_ADDRESS_FIELDS = [
  "recipient_name",
  "street",
  "street_line_2",
  "postal_code",
  "city",
  "country",
  "phone",
] as const

interface Props {
  availableMemberships: PreviewCreator[]
  /** Memberships already on this mission — disabled, with a reason (AC6). */
  alreadyAssignedIds: number[]
  /** `undefined` on a cold visit, `null` until a creator is picked. */
  offer?: EnrolOffer | null
  answers: OnBehalfAnswers
  /**
   * ⚠️ AN UPDATER, not a value — and that is load-bearing, not style.
   *
   * With `(next: OnBehalfAnswers) => void`, every handler closed over the
   * `answers` PROP from its render. Seven address fields filled without a
   * re-render between them therefore each wrote `{...answers.address, [f]: v}`
   * from the SAME stale base, and only the last one survived. Measured in the
   * browser: the enrol POST carried
   * `{"recipient_name":"","street":"",…,"phone":"+33 6 55 44 33 22"}` — six
   * empty fields and the last one set — which the server correctly refused.
   */
  onChange: (updater: (prev: OnBehalfAnswers) => OnBehalfAnswers) => void
  /** Field errors from the Inertia bag, keyed by attribute. */
  errors: Record<string, string | string[] | undefined>
  testId?: string
}

export function MissionAssignOnBehalfControl({
  availableMemberships,
  alreadyAssignedIds,
  offer,
  answers,
  onChange,
  errors,
  testId = "mission-assign-on-behalf-control",
}: Props) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")

  const assignedSet = useMemo(
    () => new Set(alreadyAssignedIds),
    [alreadyAssignedIds],
  )

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (needle === "") return availableMemberships
    return availableMemberships.filter(
      (m) =>
        (m.handle ?? "").toLowerCase().includes(needle) ||
        (m.name ?? "").toLowerCase().includes(needle),
    )
  }, [availableMemberships, search])

  const products = offer?.products ?? []
  const soleProduct = products.length === 1 ? products[0] : null
  const chosenProduct =
    products.find((p) => p.mission_product_id === answers.missionProductId) ??
    null

  const creatorLabel = labelFor(
    availableMemberships.find((m) => m.id === answers.crewMembershipId),
  )

  // ⚠️ Each step's visibility is derived from the ANSWER before it, never from
  // a step index. An index drifts the moment a step becomes conditional — and
  // the product step already is (PRD OQ8).
  const showProduct = answers.crewMembershipId !== null && products.length > 0
  const showVariant = showProduct && (chosenProduct?.variant_step ?? false)
  const showAddress =
    showProduct &&
    answers.missionProductId !== null &&
    (!showVariant || answers.productVariantId !== null)

  // ⚠️ THE PREFILL IS A COPY, NOT A DISPLAY. Without this the card rendered
  // « Pré-rempli depuis le profil de X — modifiable » above SIX EMPTY INPUTS:
  // the line keyed off `profile_address` being present while the inputs read
  // `answers.address`, which nothing ever seeded. The brand submitted an
  // address they had been told was already there and got a validation refusal
  // naming every field — on the majority path, a creator WITH a saved address.
  //
  // ⚠️ Three layers were green on it. The server ships `profile_address`
  // (`oks1042_assign_props_spec`), the component renders correctly GIVEN props
  // (Vitest), and the E2E only caught it because it reads what the form
  // actually posts. Server-correct + component-correct, field still blank.
  //
  // ⚠️ Keyed on the OFFER'S creator, not on a mount: the offer arrives on a
  // partial reload AFTER the address fieldset can exist, so a mount-time seed
  // runs too early. The ref makes it once-per-creator, so a brand's typing is
  // never clobbered by a re-render — and picking a DIFFERENT creator re-seeds,
  // which is what you want: their address, not the last one's.
  const seededFor = useRef<number | null>(null)
  useEffect(() => {
    const id = offer?.crew_membership_id ?? null
    if (id === null || seededFor.current === id) return

    seededFor.current = id
    const profile = offer?.profile_address ?? null
    // ⚠️ The SOLE product is recorded here too, not only in `pickCreator`.
    // `pickCreator` runs on the creator click, BEFORE the partial reload that
    // brings the offer — so `products` is empty, `soleProduct` is null, and
    // `missionProductId` stays null. On a one-product drop nothing else ever
    // sets it: the card renders « Cette mission propose un seul produit : X »
    // and the ADDRESS STEP NEVER APPEARS, because it waits on a product the
    // brand has no control to pick. F2/OQ8 is exactly that shape.
    const sole =
      offer?.products.length === 1 ? offer.products[0].mission_product_id : null
    onChange((prev) => ({
      ...prev,
      missionProductId: sole ?? prev.missionProductId,
      address: profile
        ? Object.fromEntries(
            ON_BEHALF_ADDRESS_FIELDS.map((f) => [f, profile[f] ?? ""]),
          )
        : {},
    }))
  }, [offer, onChange])

  function patch(next: Partial<OnBehalfAnswers>) {
    onChange((prev) => ({ ...prev, ...next }))
  }

  // Picking a creator re-opens every later step: the offer prop is fetched per
  // creator, and a stale variant id from a previous pick would post a variant
  // the new product does not offer.
  function pickCreator(id: number) {
    onChange((prev) => ({
      crewMembershipId: id,
      missionProductId: soleProduct?.mission_product_id ?? null,
      productVariantId: null,
      address: prev.address,
    }))
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-6">
      {/* ---------------------------------------------- 1. creator */}
      <fieldset className="flex flex-col gap-3">
        <legend className="text-foreground text-sm font-medium">
          {t("missions.assign.on_behalf.creator_legend")}
        </legend>

        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("missions.assign.handpick.search_placeholder")}
          data-testid="on-behalf-creator-search"
        />

        {filtered.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("missions.assign.handpick.empty_results")}
          </p>
        ) : (
          <RadioGroup
            name="crew_membership_id"
            value={answers.crewMembershipId?.toString() ?? undefined}
            onValueChange={(v) => pickCreator(Number(v))}
            className="flex flex-col gap-1"
          >
            {filtered.map((m) => {
              const disabled = assignedSet.has(m.id)
              const id = `on-behalf-creator-${m.id}`
              return (
                <label
                  key={m.id}
                  htmlFor={id}
                  data-testid={`on-behalf-creator-row-${m.id}`}
                  data-disabled={disabled ? "true" : "false"}
                  className={cn(
                    "flex min-h-11 items-center gap-3 rounded-md border px-3 py-2 text-sm",
                    disabled
                      ? "cursor-not-allowed opacity-60"
                      : "hover:bg-accent cursor-pointer",
                  )}
                >
                  <RadioGroupItem
                    id={id}
                    value={m.id.toString()}
                    disabled={disabled}
                    aria-describedby={disabled ? `${id}-reason` : undefined}
                  />
                  <span className="flex-1 break-words">{labelFor(m)}</span>
                  {disabled && (
                    // ⚠️ The REASON, not just a disabled control. A row that is
                    // dead and silent sends the brand hunting for a creator who
                    // is already fine (PRD OQ7).
                    <span
                      id={`${id}-reason`}
                      className="text-muted-foreground text-xs"
                    >
                      {t("missions.assign.on_behalf.already_assigned_row")}
                    </span>
                  )}
                </label>
              )
            })}
          </RadioGroup>
        )}
        <FieldError message={errors.crew_membership_id} />
      </fieldset>

      {/* ---------------------------------------------- 2. product */}
      {showProduct &&
        (soleProduct ? (
          // PRD OQ8 — a single-product drop has no product step. The card says
          // which product will be sent rather than rendering a one-option radio.
          <p
            className="text-muted-foreground text-sm"
            data-testid="on-behalf-product-sole"
          >
            {t("missions.assign.on_behalf.product_sole", {
              product: soleProduct.name,
            })}
          </p>
        ) : (
          <fieldset className="flex flex-col gap-3">
            <legend className="text-foreground text-sm font-medium">
              {t("missions.assign.on_behalf.product_legend")}
            </legend>
            <RadioGroup
              name="mission_product_id"
              value={answers.missionProductId?.toString() ?? undefined}
              onValueChange={(v) =>
                patch({
                  missionProductId: Number(v),
                  productVariantId: null,
                })
              }
              className="grid gap-3 lg:grid-cols-2"
            >
              {products.map((p) => (
                <ProductCard
                  key={p.mission_product_id}
                  product={p}
                  offer={offer ?? null}
                />
              ))}
            </RadioGroup>
            <FieldError message={errors.mission_product_id} />
          </fieldset>
        ))}

      {/* ---------------------------------------------- 3. variant */}
      {showVariant && chosenProduct && (
        <fieldset className="flex flex-col gap-3">
          <legend className="text-foreground text-sm font-medium">
            {t("missions.assign.on_behalf.variant_legend")}
          </legend>
          <RadioGroup
            name="product_variant_id"
            value={answers.productVariantId?.toString() ?? undefined}
            onValueChange={(v) => patch({ productVariantId: Number(v) })}
            className="flex flex-col gap-1"
          >
            {chosenProduct.variants.map((v) => {
              const id = `on-behalf-variant-${v.id}`
              return (
                <label
                  key={v.id}
                  htmlFor={id}
                  data-testid={id}
                  className="hover:bg-accent flex min-h-11 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm"
                >
                  <RadioGroupItem id={id} value={v.id.toString()} />
                  <span>{v.title ?? v.sku}</span>
                </label>
              )
            })}
          </RadioGroup>
          <FieldError message={errors.product_variant_id} />
        </fieldset>
      )}

      {/* ---------------------------------------------- 4. address */}
      {showAddress && (
        <fieldset className="flex flex-col gap-3">
          <legend className="text-foreground text-sm font-medium">
            {t("missions.assign.on_behalf.address_legend")}
          </legend>
          <p
            className="text-muted-foreground text-xs"
            data-testid="on-behalf-address-provenance"
          >
            {offer?.profile_address
              ? t("missions.assign.on_behalf.address_prefilled", {
                  creator: creatorLabel,
                })
              : t("missions.assign.on_behalf.address_empty", {
                  creator: creatorLabel,
                })}
          </p>

          <div className="grid gap-3 md:grid-cols-2">
            {ON_BEHALF_ADDRESS_FIELDS.map((field) => (
              <div key={field} className="flex flex-col gap-1">
                <Label htmlFor={`on-behalf-${field}`}>
                  {t(`collabs.shipping_address.${field}`)}
                </Label>
                <Input
                  id={`on-behalf-${field}`}
                  name={field}
                  data-testid={`on-behalf-address-${field}`}
                  value={answers.address[field] ?? ""}
                  onChange={(e) => {
                    // ⚠️ Reads `prev.address`, never the captured prop. See the
                    // note on `onChange` — the prop version lost six of seven.
                    const value = e.target.value
                    onChange((prev) => ({
                      ...prev,
                      address: { ...prev.address, [field]: value },
                    }))
                  }}
                  aria-invalid={errors[field] ? true : undefined}
                />
                <FieldError message={errors[field]} />
              </div>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  )
}

/**
 * One offered product, with its warnings as TEXT UNDER THE CARD.
 *
 * ⚠️ `answerable` and `flag` are read as two separate facts. `answerable:
 * false` means the recency guard could not answer for this product; `true`
 * with a null flag means it ran and flagged nobody. Both render as silence,
 * and collapsing them would turn "we don't know" into "all clear" — the
 * failure `Missions::GiftRecencyGuard`'s own docstring is written against.
 */
function ProductCard({
  product,
  offer,
}: {
  product: EnrolOfferProduct
  offer: EnrolOffer | null
}) {
  const { t } = useTranslation()
  const id = `on-behalf-product-${product.mission_product_id}`

  // OKS-1042 F3 — ONE shared derivation, so the confirm dialog cannot show a
  // different set of warnings than this card (see `lib/on-behalf-warnings`).
  const warnings = productWarnings(product, offer)
  const { exhausted, overBy, recencyFlag } = warnings
  const alreadyHas = recencyFlag !== null

  const describedBy =
    [exhausted ? `${id}-stock` : null, alreadyHas ? `${id}-recency` : null]
      .filter(Boolean)
      .join(" ") || undefined

  return (
    <label
      htmlFor={id}
      data-testid={id}
      className="hover:bg-accent flex cursor-pointer flex-col gap-2 rounded-lg border p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold break-words">
          {product.name}
        </span>
        {/* ⚠️ Selectable, never disabled — R5 is warn-never-refuse. */}
        <RadioGroupItem
          id={id}
          value={product.mission_product_id.toString()}
          aria-describedby={describedBy}
        />
      </div>

      {/*
        ⚠️ REUSED VERBATIM from the assign preview (PRD OQ5), and the key names
        were MEASURED rather than guessed — my first draft invented
        `gift_slots_exhausted`, which does not exist. The real pair is
        `gift_slots_product_exhausted` / `_product_over`, both of which
        interpolate `%{name}`. Two wordings for one fact on one screen reads as
        two facts.

        ⚠️ `_product_over` is a DIFFERENT fact from `_exhausted`, not a louder
        one: remaining is clamped at 0, so an overshot product reads "épuisé"
        unless the overshoot is named (OKS-990 R14b).
      */}
      {overBy > 0 || exhausted ? (
        <span
          id={`${id}-stock`}
          data-testid={overBy > 0 ? `${id}-over` : `${id}-exhausted`}
          className="flex items-center gap-1.5"
        >
          <Badge variant="outline">{stockSentence(t, product, warnings)}</Badge>
        </span>
      ) : null}

      {/*
        The same four-way branch the preview uses (`today_*` vs the counted
        pair, variant vs product-only) rather than a second expression of it:
        `days_ago === 0` is its own sentence in FR, and a `count: 0` would
        render "il y a 0 j".
      */}
      {alreadyHas && recencyFlag && (
        <span
          id={`${id}-recency`}
          data-testid={`${id}-already-has`}
          className="text-muted-foreground flex items-start gap-1.5 text-xs"
        >
          <History className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span className="break-words">{recencySentence(t, recencyFlag)}</span>
        </span>
      )}
    </label>
  )
}

function FieldError({ message }: { message?: string | string[] }) {
  if (!message) return null
  const text = Array.isArray(message) ? message[0] : message
  return (
    <p className="text-destructive text-xs" role="alert">
      {text}
    </p>
  )
}

function labelFor(m?: PreviewCreator): string {
  if (!m) return ""
  return m.handle ?? m.name ?? ""
}
